import GSAP from 'gsap';

import Component from '@averyano/core/src/classes/Component';

// Input surface for the portfolio gallery. Under the all-WebGL layout this no
// longer owns any figures — it's a single full-viewport drag/wheel catcher that
// produces a smoothed `scroll` (+ direction) and a responsive `scale`. The WebGL
// Gallery reads `scroll`/`scale` each frame and positions + wraps every plane on
// the GPU (see app/pages/Portfolio/Canvas/Gallery). Layout lives in layout.js.
export default class MultidimensionalGallery extends Component {
	constructor() {
		super({
			element: '.multidim-gallery',
			elements: {},
		});

		this.inView = true;
		this.isDown = false;

		this.frame = null;
		this.loopBound = this.loop.bind(this);

		this.x = { current: 0, target: 0, lerp: 0.1, direction: 'left' };
		this.y = { current: 0, target: 0, lerp: 0.1, direction: 'bottom' };
		this.scrollCurrent = { x: 0, y: 0 };
		this.scroll = { x: 0, y: 0 };
		this.touch = {
			x: { start: 0, end: 0 },
			y: { start: 0, end: 0 },
		};

		this.onTouchDownBound = this.onTouchDown.bind(this);
		this.onTouchMoveBound = this.onTouchMove.bind(this);
		this.onTouchUpBound = this.onTouchUp.bind(this);
		this.onWheelBound = this.onWheel.bind(this);
	}

	create() {
		super.createComponent();
		this.checkMobile();
		this.addEventListeners();
	}

	show() {}
	hide() {}

	// Responsive scale only — the surface itself is NOT transformed (no scaled DOM
	// container under all-WebGL). The Gallery multiplies layout px by this scale.
	checkMobile() {
		const phone = window.matchMedia('(max-width: 767px)');
		const fourK = window.matchMedia('(min-width: 3840px)');
		this.isMobile = phone.matches;

		if (this.isMobile) this.scale = 1;
		else if (fourK.matches) this.scale = 2.25;
		else this.scale = 1.5;
	}

	onResize() {
		this.checkMobile();
		this.scroll.x = this.x.target = this.x.current = 0;
		this.scroll.y = this.y.target = this.y.current = 0;
	}

	addEventListeners() {
		this.element.addEventListener('mousedown', this.onTouchDownBound);
		window.addEventListener('mousemove', this.onTouchMoveBound);
		window.addEventListener('mouseup', this.onTouchUpBound);
		this.element.addEventListener('touchstart', this.onTouchDownBound, {
			passive: false,
		});
		window.addEventListener('touchmove', this.onTouchMoveBound, {
			passive: false,
		});
		window.addEventListener('touchend', this.onTouchUpBound);
		this.element.addEventListener('wheel', this.onWheelBound, {
			passive: false,
		});
	}

	removeEventListeners() {
		this.element.removeEventListener('mousedown', this.onTouchDownBound);
		window.removeEventListener('mousemove', this.onTouchMoveBound);
		window.removeEventListener('mouseup', this.onTouchUpBound);
		this.element.removeEventListener('touchstart', this.onTouchDownBound);
		window.removeEventListener('touchmove', this.onTouchMoveBound);
		window.removeEventListener('touchend', this.onTouchUpBound);
		this.element.removeEventListener('wheel', this.onWheelBound);
	}

	onTouchDown(event) {
		this.isDown = true;
		this.startLoop();

		this.touch.x.start = event.touches
			? event.touches[0].clientX
			: event.clientX;
		this.touch.y.start = event.touches
			? event.touches[0].clientY
			: event.clientY;

		this.scrollCurrent.x = this.scroll.x;
		this.scrollCurrent.y = this.scroll.y;
	}

	onTouchMove(event) {
		if (!this.isDown) return;

		const x = event.touches ? event.touches[0].clientX : event.clientX;
		const y = event.touches ? event.touches[0].clientY : event.clientY;

		this.touch.x.end = x;
		this.touch.y.end = y;

		const xDistance = this.touch.x.start - this.touch.x.end;
		const yDistance = this.touch.y.start - this.touch.y.end;

		this.x.target = this.scrollCurrent.x - xDistance;
		this.y.target = this.scrollCurrent.y - yDistance;
		this.startLoop();
	}

	onTouchUp() {
		this.isDown = false;
	}

	onWheel(event) {
		event.preventDefault();
		this.x.target -= event.deltaX;
		this.y.target -= event.deltaY;
		this.startLoop();
	}

	startLoop() {
		if (this.frame) return;
		this.frame = requestAnimationFrame(this.loopBound);
	}

	stopLoop() {
		if (this.frame) cancelAnimationFrame(this.frame);
		this.frame = null;
	}

	loop() {
		const active = this.update();
		if (active) {
			this.frame = requestAnimationFrame(this.loopBound);
		} else {
			this.stopLoop();
		}
	}

	// Lerp scroll toward the drag/wheel target; the WebGL Gallery consumes
	// `this.scroll` and does all positioning + the infinite wrap on the GPU.
	update() {
		if (!this.inView) return false;

		this.x.current = GSAP.utils.interpolate(
			this.x.current,
			this.x.target,
			this.x.lerp
		);
		this.y.current = GSAP.utils.interpolate(
			this.y.current,
			this.y.target,
			this.y.lerp
		);

		const dx = Math.abs(this.x.target - this.x.current);
		const dy = Math.abs(this.y.target - this.y.current);
		const active = this.isDown || dx > 0.01 || dy > 0.01;
		if (!active) {
			this.x.current = this.x.target;
			this.y.current = this.y.target;
		}

		if (this.scroll.x < this.x.current) this.x.direction = 'right';
		else if (this.scroll.x > this.x.current) this.x.direction = 'left';

		if (this.scroll.y < this.y.current) this.y.direction = 'top';
		else if (this.scroll.y > this.y.current) this.y.direction = 'bottom';

		this.scroll.x = this.x.current;
		this.scroll.y = this.y.current;

		return active;
	}

	destroy() {
		this.removeEventListeners();
		this.stopLoop();
	}
}
